import type { GeneratedFile } from "@/lib/types";

/**
 * PANDA-1917 — one measurement, one ceiling, for every path that POSTs a file
 * set.
 *
 * Reported: a 47-file project failed to preview with
 * `files write failed (chunk 2/3): 413`, while the DB save failed with a bare
 * `API error 413`. Both are PLATFORM 413s — Vercel rejects an over-cap body at
 * the edge, before the route runs, so no handler of ours ever sees the request
 * and no amount of server-side handling can catch it. The only defence is
 * measuring the body correctly before sending it.
 *
 * Three things were wrong, and they compounded:
 *
 *   1. This module measured `String.length` — UTF-16 code units. A file of
 *      emoji, CJK or accented text under-counts by up to 3x against the UTF-8
 *      bytes the transport actually weighs. lib/sandbox/payload.ts had already
 *      hit this and fixed it there; the DB-save chunker never got the same fix.
 *
 *   2. NEITHER path modelled JSON escaping. The body is `JSON.stringify`'d, and
 *      every `"`, `\` and newline becomes two characters on the wire. The
 *      sandbox module's comment said its headroom "absorbs JSON escaping", but
 *      that was a hope rather than a measurement — and escape density is a
 *      property of the content, so it is exactly the kind of thing that holds
 *      for hand-written TSX and breaks on a generated data file.
 *
 *   3. Three budgets — 3.0MB, 3.5MB, 3.8MB — sat against one real ceiling, none
 *      derived from it and none derived from each other.
 *
 * The fix for (1) and (2) together is to stop estimating. `JSON.stringify` on a
 * single string gives the exact escaped, quoted form, and its UTF-8 length is
 * precisely what that file costs on the wire. Files are individually capped at
 * 200KB upstream, so measuring them exactly is cheap and removes the entire
 * class of "the estimate was optimistic" failure.
 */

/**
 * Vercel rejects a serverless request body over ~4.5MB at the edge. This is the
 * hard ceiling every budget below is derived from — it is not ours to raise.
 */
export const PLATFORM_BODY_LIMIT_BYTES = 4_500_000;

/**
 * What a single request may carry. The margin covers what the per-file
 * measurement deliberately does NOT include: request headers, cookies, the
 * enclosing `{"projectId":"…","files":[…]}` scaffolding, and any wrapper a
 * caller adds. Being exact about the files means this margin can be a genuine
 * safety buffer rather than an allowance for unmodelled escaping.
 */
export const SAFE_BODY_BYTES = 3_800_000;

/**
 * UTF-8 byte length — what the transport measures. `.length` counts UTF-16 code
 * units and under-counts multi-byte content.
 */
export function byteLength(s: string): number {
  return typeof TextEncoder !== "undefined"
    ? new TextEncoder().encode(s).length
    : // Fallback (should never run in a browser): worst-case 4 bytes/char.
      s.length * 4;
}

/**
 * The EXACT bytes one file contributes to a `{ files: [...] }` body.
 *
 * `JSON.stringify` of a string yields its quoted, escaped wire form, so this is
 * a measurement rather than an estimate: no assumption about how quote-dense
 * the content is, and multi-byte characters are counted as UTF-8. The `+ 24`
 * covers the object's own punctuation — `{"path":,"content":},` — which is
 * fixed and small.
 *
 * Falls back to a pessimistic bound if stringify ever throws (it cannot for a
 * string, but this runs on user content and must not be the thing that breaks).
 */
export function serializedFileBytes(file: GeneratedFile): number {
  try {
    return (
      byteLength(JSON.stringify(file.content ?? "")) +
      byteLength(JSON.stringify(file.path ?? "")) +
      24
    );
  } catch {
    return (
      (byteLength(file.content ?? "") + byteLength(file.path ?? "")) * 2 + 24
    );
  }
}

/** The exact serialized size of a whole `{ files: [...] }` body, plus a small
 *  base for the wrapper. Used to decide whether a set needs splitting at all. */
export function serializedFilesBodyBytes(files: GeneratedFile[]): number {
  let n = 128;
  for (const f of files) n += serializedFileBytes(f);
  return n;
}

/**
 * Splits a file set into batches whose serialized body stays under `maxBytes`.
 *
 * Each file is persisted server-side as its own versioned row, so splitting
 * across requests is lossless — a partial batch commits independently. Order is
 * preserved; an empty input yields an empty list.
 *
 * A single file larger than `maxBytes` cannot be split at file granularity, so
 * it is emitted alone and WILL still fail at the edge. That is a real limit, not
 * an oversight: callers must report it as such rather than presenting it as a
 * retryable transport hiccup, because retrying is guaranteed to fail. See
 * `oversizeFiles` for identifying them before the send.
 */
export function chunkFilesBySize(
  files: GeneratedFile[],
  maxBytes: number,
): GeneratedFile[][] {
  const batches: GeneratedFile[][] = [];
  let current: GeneratedFile[] = [];
  let currentBytes = 128;

  for (const file of files) {
    const size = serializedFileBytes(file);
    // Close the current batch before it would exceed the budget — but never
    // emit an empty batch, so a lone oversized file still ships (on its own).
    if (current.length > 0 && currentBytes + size > maxBytes) {
      batches.push(current);
      current = [];
      currentBytes = 128;
    }
    current.push(file);
    currentBytes += size;
  }
  if (current.length > 0) batches.push(current);

  return batches;
}

/**
 * Files that cannot fit in any request on their own. Chunking cannot help these
 * — the caller needs to say so plainly instead of offering a retry that cannot
 * succeed.
 */
export function oversizeFiles(
  files: GeneratedFile[],
  maxBytes: number,
): GeneratedFile[] {
  return files.filter((f) => serializedFileBytes(f) + 128 > maxBytes);
}
